import type { Paginated } from './pagination';

/**
 * This application only reads from Zoho People and writes to none of the systems it reads:
 *
 *   Entra ID  ──sign-in──▶  this application
 *   Zoho People  ──zoho_to_app──▶  user-service
 *
 * Entra ID decides who may sign in. Zoho People is the source of
 * what HR owns: name, email, department, reporting manager and departure. It
 * keeps authority over departure, because only `user.deactivated` revokes a
 * session that is already live. user-service owns authorization.
 *
 * `link_zoho_hires` writes nothing to Zoho either. HR creates a hired person in
 * Zoho; the job looks each waiting employee up there and links them when Zoho's
 * `EmployeeID` is the `EMP-` reference. `app_to_zoho_hires` is the name the same
 * job had while it wrote to Zoho; it stays so runs already recorded keep a name.
 *
 * `retire_zoho_mock` is a one-off, run by an administrator when an environment moves from the
 * Zoho mock to real Zoho: it removes the mock's fictional people, by an explicit list of their
 * ids and never by absence from a read.
 */
export type SyncJobName =
  | 'zoho_to_app'
  | 'app_to_zoho_hires'
  | 'link_zoho_hires'
  | 'retire_zoho_mock'
  | 'reconcile_hires'
  | 'purge_sync_events';

/** The jobs that can be started now; `app_to_zoho_hires` is history only. */
export const RUNNABLE_SYNC_JOBS = [
  'zoho_to_app',
  'link_zoho_hires',
  'retire_zoho_mock',
] as const satisfies readonly SyncJobName[];

/**
 * Every job the service records, the ones the jobs console lists. `reconcile_hires` and
 * `purge_sync_events` are scheduled housekeeping; only the first can be started by hand.
 */
export const REGISTERED_SYNC_JOBS = [
  'zoho_to_app',
  'link_zoho_hires',
  'reconcile_hires',
  'purge_sync_events',
  'retire_zoho_mock',
] as const satisfies readonly SyncJobName[];

/** Jobs an administrator may start through `POST /directory/sync/jobs/:job/runs`. */
export const MANUAL_START_SYNC_JOBS = [
  'zoho_to_app',
  'link_zoho_hires',
  'reconcile_hires',
  'retire_zoho_mock',
] as const satisfies readonly SyncJobName[];

export const ALL_SYNC_JOBS = [
  ...REGISTERED_SYNC_JOBS,
  'app_to_zoho_hires',
] as const satisfies readonly SyncJobName[];

export const SYNC_RUN_STATUSES = ['running', 'succeeded', 'partial', 'failed', 'refused'] as const;

/**
 * `refused` is a designed no-op, produced only by `retire_zoho_mock` when it is pointed at the
 * Zoho mock or sync is switched off. It is not a failure and does not count towards a job's
 * failure streak. (`zoho_to_app` with Zoho disabled is `succeeded`, with a skipped event.)
 */
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];

export const SYNC_RUN_TRIGGERS = ['schedule', 'manual'] as const;

/** `schedule` for the cron, `manual` for an administrator (or the CLI) starting it. */
export type SyncRunTrigger = (typeof SYNC_RUN_TRIGGERS)[number];

/**
 * The error text of a run swept after a restart or found hung; the status is `failed`. The
 * migration that first sweeps stale rows writes this same string. Such a run is an interruption,
 * not a failing job, so it is left out of `consecutiveFailures`.
 */
export const SYNC_RUN_STALE_ERROR = 'STALE: the service restarted during the run';
export interface SyncRunReport {
  id: string;
  job: SyncJobName;
  status: SyncRunStatus;
  inspected: number;
  created: number;
  updated: number;
  deactivated: number;
  unchanged: number;
  failed: number;
  dryRun: boolean;
  startedAt: string;
  finishedAt: string | null;
  /** Watermark handed to the next run so it only reads what changed. */
  cursor: string | null;
  error: string | null;
  trigger: SyncRunTrigger;
  /** The user id of whoever started a manual run; null for the schedule and for the CLI. */
  triggeredBy: string | null;
  /** True when the run ignored the watermark and read everything (zoho_to_app only). */
  fullResync: boolean;
}

export interface RunSyncJobRequest {
  job: SyncJobName;
  /** Ignore the stored watermark and re-read everything. */
  fullResync?: boolean;
  dryRun?: boolean;
  /** Set by the gateway from the session, never from the request body. */
  triggeredBy?: string | null;
}

/** Fire and return: `POST /directory/sync/jobs/:job/runs`. */
export interface StartSyncRunRequest {
  job: SyncJobName;
  fullResync?: boolean;
  dryRun?: boolean;
  /** Set by the gateway from the session, never from the request body. */
  triggeredBy: string | null;
}

export interface StartSyncRunResult {
  runId: string;
}

export type SyncJobGroup = 'scheduled' | 'one-off';

/** A run in a job's summary or the history list, with the person who started it named when known. */
export interface SyncRunSummary extends SyncRunReport {
  /** Full name of `triggeredBy` when user-service could name them; null otherwise. */
  triggeredByName: string | null;
}

export interface SyncJobSchedule {
  /** Six-field cron expression, in the host's zone (UTC where deployed). */
  cron: string;
  /** The schedule in words, always with its zone. */
  words: string;
  /** Null while the schedule is switched off. */
  nextRunAt: string | null;
}

/** One entry of `GET /directory/sync/jobs`. */
export interface SyncJobInfo {
  job: SyncJobName;
  title: string;
  description: string;
  group: SyncJobGroup;
  /** Null for a one-off job, which has no schedule. */
  schedule: SyncJobSchedule | null;
  /** The extra daily full read of zoho_to_app; null for every other job. */
  fullResyncSchedule: SyncJobSchedule | null;
  /** Whether the schedule fires: the flag below is on, or the job is housekeeping that is always on. False for a one-off. */
  enabled: boolean;
  /** The environment variable that switches the schedule; null when always on or one-off. */
  enabledBy: string | null;
  /** Soonest of this job's schedules; null when disabled or one-off. */
  nextRunAt: string | null;
  manualStart: boolean;
  supportsDryRun: boolean;
  supportsFullResync: boolean;
  /** The newest run that was not a dry run; null when there is none. */
  lastRun: SyncRunSummary | null;
  /** When the newest succeeded (or partial) real run started. */
  lastSuccessAt: string | null;
  /** Real runs in a row, newest first, that ended failed or partial. `refused` runs and runs swept as STALE neither count nor break the streak. */
  consecutiveFailures: number;
  running: boolean;
  runningRunId: string | null;
  /** Enabled, and nothing has started within twice the schedule's interval. */
  overdue: boolean;
}

export interface SyncJobsReport {
  jobs: SyncJobInfo[];
}

export interface ListSyncRunHistoryRequest {
  job?: SyncJobName;
  status?: SyncRunStatus;
  trigger?: SyncRunTrigger;
  /** ISO timestamps; `from` is inclusive and `to` exclusive, on `startedAt`. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface SyncStatusReport {
  jobs: {
    job: SyncJobName;
    lastRunAt: string | null;
    lastStatus: SyncRunStatus | null;
    cursor: string | null;
    consecutiveFailures: number;
  }[];
}

export const SYNC_EVENT_LEVELS = ['info', 'warn', 'error'] as const;

export type SyncEventLevel = (typeof SYNC_EVENT_LEVELS)[number];

export const SYNC_EVENT_ACTIONS = [
  'created',
  'updated',
  'deactivated',
  'linked',
  'conflict',
  'skipped',
  'failed',
] as const;

export type SyncEventAction = (typeof SYNC_EVENT_ACTIONS)[number];

/**
 * One line of a run: a person or record it acted on or failed on. The subject is a
 * Zoho employee id or an `EMP-` reference, never an email, and the message is built
 * from codes, so nothing here can carry an address, a token or a response body.
 */
export interface SyncRunEventReport {
  id: string;
  runId: string;
  job: SyncJobName;
  at: string;
  level: SyncEventLevel;
  action: SyncEventAction;
  subject: string;
  /** A stable machine-readable reason, such as ZOHO_REQUEST_FAILED. */
  code: string | null;
  message: string;
}

export interface GetSyncRunRequest {
  id: string;
  page?: number;
  pageSize?: number;
  /** Narrow the events; each is optional and they combine. */
  level?: SyncEventLevel;
  action?: SyncEventAction;
  code?: string;
}

export interface SyncRunDetail {
  run: SyncRunSummary;
  events: Paginated<SyncRunEventReport>;
}

/** Events older than this are deleted by the service on a schedule. */
export const SYNC_EVENT_RETENTION_DAYS = 90;

/** Why a row is on the Conflicts tab; the SPA explains each, and an unknown one is shown as given. */
export const SYNC_CONFLICT_CODES = {
  missingFromView: 'ZOHO_RECORD_MISSING_FROM_VIEW',
  hireRecordDeparted: 'ZOHO_RECORD_DEPARTED',
  hireLinkConflict: 'HIRE_LINK_CONFLICT',
} as const;

export type SyncConflictKind = 'missing_from_view' | 'hire_link';

/** One person the Zoho sync cannot keep up to date, or a hire HR has to link by hand. */
export interface SyncConflict {
  kind: SyncConflictKind;
  code: string;
  /** The person's full name, never an email. Null when user-service could not be asked. */
  fullName: string | null;
  /** The Zoho employee id for a missing person, the `EMP-` reference for a hire. */
  subject: string;
  /** The platform user, when there is one, for the link to the platform app. */
  appUserId: string | null;
  /** When the person was first found missing, or the hire record last changed. */
  since: string;
  /** The problem in words. */
  reason: string;
  /** What a person should do about it, in plain English. */
  action: string;
}

export interface SyncConflictsReport {
  /** When a full read last compared the view with the people already known; null if none has. */
  lastFullReadAt: string | null;
  items: SyncConflict[];
}
